import { Alchemy, OwnedToken as AlchemyOwnedToken, Network, NftOrdering } from 'alchemy-sdk'
import {
  ERC20,
  OwnedERC20,
  OwnedNFT,
  NFT,
} from 'src/common/types/models/Tokens'
import { big, ZERO } from 'src/common/utils/big-utils'
import staticConfig from 'src/config'
import {
  normalizeAlchemyNftContractMetadata,
  normalizeAlchemyOwnedERC20,
  normalizeAlchemyTokenMetadata,
  normalizeAllNftsForOwner,
  testIsSpamToken,
} from './utils'
import Big from 'big.js'
import { NetworkId, whitelabeledTokens } from 'src/common/consts'

const CHAIN_ID_NAME: Record<number, Network> = {
  421614: Network.ARB_SEPOLIA,
  1: Network.ETH_MAINNET,
  137: Network.MATIC_MAINNET,
  8453: Network.BASE_MAINNET,
}
export const a = () => {}
const { alchemyApiKey } = staticConfig

export const getAllTokensForOwner = async (
  address?: string,
  contractAddresses?: string[],
  network?: number
): Promise<OwnedERC20[]> => {
  if (!address || !network) return []
  const alchemy = new Alchemy({
    apiKey: alchemyApiKey,
    network: CHAIN_ID_NAME[network],
  })

  const tokens: AlchemyOwnedToken[] = []
  const whitelabeled = whitelabeledTokens[network as NetworkId]

  let pageKey: string | undefined
  do {
    const response = await alchemy.core.getTokensForOwner(address, {
      pageKey,
      contractAddresses: whitelabeled,
    })
    tokens.push(...response.tokens)
    pageKey = response.pageKey
  } while (pageKey)

  const normalizedTokens = tokens
    .filter(
      token =>
        big(token.balance).gt(ZERO) &&
        !testIsSpamToken(token.name, token.symbol)
    )
    .map(token => normalizeAlchemyOwnedERC20(token, network))

  return normalizedTokens
}

export const getBalanceOfERC20 = async (
  address: string | undefined,
  contractAddress: string | undefined,
  network: number | undefined
): Promise<Big | null> => {
  if (!address || !network || !contractAddress) return null
  const alchemy = new Alchemy({
    apiKey: alchemyApiKey,
    network: CHAIN_ID_NAME[network],
  })

  const { tokens } = await alchemy.core.getTokensForOwner(address, {
    ...(contractAddress && { contractAddresses: [contractAddress] }),
  })

  const singleToken = tokens[0]

  const balance = singleToken?.rawBalance ? big(singleToken.rawBalance) : ZERO
  return balance
}

export const getTokenMetadata = async (
  contractAddress: string | undefined,
  network: number | undefined
): Promise<ERC20 | null> => {
  if (!contractAddress || !network) return null

  const alchemy = new Alchemy({
    apiKey: alchemyApiKey,
    network: CHAIN_ID_NAME[network],
  })

  const token = await alchemy.core.getTokenMetadata(contractAddress)
  // const tryNFT = await alchemy.nft.getContractMetadata(contractAddress)

  if (!token.decimals) return null
  return normalizeAlchemyTokenMetadata(contractAddress, token)
}

export const getNftContractMetadata = async (
  contractAddress: string | undefined,
  network: number | undefined
): Promise<NFT | null> => {
  if (!contractAddress || !network) return null

  const alchemy = new Alchemy({
    apiKey: alchemyApiKey,
    network: CHAIN_ID_NAME[network],
  })

  const metadata = await alchemy.nft.getContractMetadata(contractAddress)

  return normalizeAlchemyNftContractMetadata(contractAddress, metadata)
}

export const getNftsForOwners = async (
  owner: string | undefined,
  contractAddresses: string[] | undefined,
  network: number | undefined
): Promise<{
  total: number
  nfts: OwnedNFT[] | null
} | null> => {
  if (!contractAddresses || !network || !owner) return null

  const alchemy = new Alchemy({
    apiKey: alchemyApiKey,
    network: CHAIN_ID_NAME[network],
  })

  const { ownedNfts, totalCount } = await alchemy.nft.getNftsForOwner(owner, {
    contractAddresses,
    orderBy: NftOrdering.TRANSFERTIME
  })
  return {
    nfts: normalizeAllNftsForOwner(ownedNfts),
    total: totalCount,
  }
}
